import { useEffect, useRef } from 'react';

/**
 * Refresh the Hotjar survey flags when a purchase that landed on Complete still settling
 * (e.g. a pending Pix) gets completed by the polling.
 * Only a visit that arrived from checkout (`trackPurchase`) qualifies: reopening the
 * confirmation later must not expose the flags again.
 * @param {object} params - Hook params.
 * @param {boolean} params.trackPurchase - Whether the URL carries `trackPurchase=true`.
 * @param {boolean} params.isTracked - Whether trackPurchaseHandler already ran.
 * @param {string} [params.purchaseStatus] - Current purchase status.
 * @param {Function} params.refreshSurveyFlags - Rewrites the URL with the current flags.
 */
export default function useSurveyFlagsOnCompletion({
  trackPurchase,
  isTracked,
  purchaseStatus,
  refreshSurveyFlags,
}) {
  const arrivedFromCheckoutRef = useRef(trackPurchase);
  const lastStatusRef = useRef(purchaseStatus);

  useEffect(() => {
    const lastStatus = lastStatusRef.current;
    lastStatusRef.current = purchaseStatus;
    const justCompleted =
      Boolean(lastStatus) && lastStatus !== 'completed' && purchaseStatus === 'completed';
    // Before trackPurchaseHandler runs there is nothing to refresh: it will read `completed` itself.
    if (arrivedFromCheckoutRef.current && isTracked && justCompleted) refreshSurveyFlags();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [purchaseStatus]);
}
